import { Icon } from '@openeducation/oe-rn-components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Image, View, Platform } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { SafeAreaView } from 'react-native-safe-area-context';

import styles from './spelling-image.style';
import { useColors } from '../../../hooks';
import { useFont } from '../../../hooks/lesson-player.context';
import { useActivityContext } from '../../../hooks/use-activity.context';
import { useErrorSound } from '../../../hooks/use-error-sound';
import { useScroll } from '../../../hooks/use-scroll';
import { useSuccessSound } from '../../../hooks/use-success-sound';
import trueBeginnerStr from '../../../utils/true-beginner-str';
import { CheckButton } from '../../check-button/check-button';
import { incorrectAnimation } from '../../choice-bubble/choice-bubble.animations';
import { ContinueButton } from '../../continue-button/continue-button';
import { InputText } from '../../input-text/input-text.component';
import { Instruction } from '../../instruction/instruction.component';
import { ThemedText } from '../../themed-text';
import { ThemedView } from '../../themed-view';

export function SpellingImage() {
  const {
    currentActivity: activity,
    currentStep,
    isCurrentStepComplete: isStepCompleted,
    goNext: goNextStep,
    completeCurrentStep: completeStep,
    currentStepIndex,
  } = useActivityContext();
  const correctSound = useSuccessSound();
  const incorrectSound = useErrorSound();
  const color = useColors();
  const fonts = useFont();

  const xRef = useRef(new Animated.Value(0));
  const scaleRef = useRef(new Animated.Value(0));
  const questionOpacityRef = useRef(new Animated.Value(1));
  const scrollRef = useRef<KeyboardAwareScrollView>(null);

  const [showNextButton, setShowNextButton] = useState(false);
  const [text, setText] = useState('');
  const [tries, setTries] = useState(0);
  const [hintVisible, setHintVisible] = useState(false);
  const [feedback, setFeedback] = useState(false);
  const [feedbackTrue, setFeedbackTrue] = useState(false);

  const { scrollToEnd, scrollToTop } = useScroll(scrollRef);

  const questionFadeOut = useCallback(() => {
    Animated.timing(questionOpacityRef.current, {
      toValue: 0,
      duration: 600,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, []);

  const questionFadeIn = useCallback(() => {
    Animated.timing(questionOpacityRef.current, {
      toValue: 1,
      duration: 400,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, []);

  const cleanUp = () => {
    setShowNextButton(false);
    setText('');
  };

  const continueWithNextStep = useCallback(() => {
    cleanUp();
    questionFadeOut();
    setTimeout(() => {
      goNextStep();
      setFeedbackTrue(false);
      questionFadeIn();
    }, 800);
  }, [goNextStep, questionFadeIn, questionFadeOut]);

  useEffect(() => {
    if (isStepCompleted) {
      setShowNextButton(true);
    }
  }, [isStepCompleted]);

  useEffect(() => {
    if (currentStep.maxHints && tries === currentStep.maxHints) {
      setHintVisible(true);
      setTries(0);
    } else if (tries === 2) {
      setHintVisible(true);
      setTries(0);
    }
  }, [currentStep.maxHints, tries]);

  useEffect(() => {
    setFeedback(false);
    setHintVisible(false);
    scrollToTop();
  }, [currentStep, currentStepIndex, scrollToTop]);

  useEffect(() => {
    if (showNextButton || hintVisible) {
      scrollToEnd();
    }
  }, [showNextButton, scrollToEnd, hintVisible]);

  const verify = () => {
    if (text.trim().toLowerCase() === currentStep.answer.trim().toLowerCase()) {
      correctSound.play();
      questionFadeOut();
      setTimeout(() => {
        setFeedbackTrue(true);
        questionFadeIn();
        completeStep();
      }, 800);
    } else {
      setFeedback(true);
      setFeedbackTrue(false);
      if (text !== '') {
        setTries((x) => x + 1);
      }
      incorrectSound.play();
      incorrectAnimation(xRef.current).start();
      setTimeout(() => {
        setFeedback(false);
      }, 800);
    }
  };

  const translateX = xRef.current.interpolate({
    inputRange: [-1, 0, 1],
    outputRange: [-5, 0, 5],
  });

  const scale = scaleRef.current.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.1],
  });

  return (
    <>
      <KeyboardAwareScrollView ref={scrollRef}>
        <SafeAreaView edges={['bottom']} style={styles.container}>
          <View style={styles.section}>
            <Instruction
              text={trueBeginnerStr(
                activity,
                'instructions',
                activity.trueBeginner
              )}
            />
            {!!currentStep?.image && (
              <Image
                style={styles.image}
                source={{ uri: currentStep?.image }}
              />
            )}

            <ThemedView style={[styles.view]}>
              {feedbackTrue && (
                <Animated.View
                  style={[styles.hint, { opacity: questionOpacityRef.current }]}
                >
                  <ThemedText
                    type="onPrimary"
                    fontSize="medium"
                    style={[{ ...fonts.primary.bold }]}
                  >
                    {currentStep?.answer}
                  </ThemedText>
                </Animated.View>
              )}
              {!feedbackTrue && (
                <Animated.View
                  style={[
                    { opacity: questionOpacityRef.current },
                    styles.inputContainer,
                  ]}
                >
                  <Animated.View
                    style={[
                      styles.inputContainer,
                      {},
                      { transform: [{ translateX }, { scale }] },
                    ]}
                  >
                    <InputText
                      feedback={feedback}
                      value={text}
                      onSubmitEditing={verify}
                      onChangeText={(x) => {
                        setText(x);
                      }}
                    />
                    {hintVisible && (
                      <View style={styles.hint}>
                        <Icon
                          color={color.onPrimary}
                          size={12}
                          name="magic"
                          type="solid"
                        />
                        <ThemedText
                          type="onPrimary"
                          fontSize="extraSmall"
                          style={[
                            styles.hintText,
                            { ...fonts.primary.regular },
                          ]}
                        >
                          Hint: {currentStep?.hint || currentStep?.answer}
                        </ThemedText>
                      </View>
                    )}
                  </Animated.View>
                </Animated.View>
              )}
            </ThemedView>
          </View>
        </SafeAreaView>
      </KeyboardAwareScrollView>

      <CheckButton isVisible={!feedbackTrue} onPress={verify} />
      {showNextButton && (
        <ContinueButton
          feedbackTitle={currentStep?.feedbackTitle}
          feedback={currentStep?.feedback}
          isCorrect={feedbackTrue}
          onPress={continueWithNextStep}
        />
      )}
    </>
  );
}
